import {useMemo, useState} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {useTranslation} from 'react-i18next';

import SelectGroup from './parts/SelectGroup';
import SelectListPaper from './parts/SelectListPaper';
import SelectOptionRow from './parts/SelectOptionRow';
import {
    type ResolvedOption,
    type SelectOption,
    filterBySearch,
    gatherGroups,
    headingKey,
    isGrouped,
    isSettled,
    optionKey,
    unknownOption
} from './parts/selectOption';

interface SingleSelectProps {
    label?: string;
    value: string;
    options: SelectOption[];
    onChange: (value: string) => void;
    /** Also supplies the accessible name when there is no label. */
    placeholder?: string;
    /** Stored name for a value absent from options. */
    unknownLabel?: string;
    /** False marks even an empty list as loaded; omit to infer from populated options. */
    loading?: boolean;
    disabled?: boolean;
    disableClearable?: boolean;
    /** Focuses the field and opens the list on mount. */
    autoFocus?: boolean;
}

const SingleSelect = ({
    label,
    value,
    options,
    onChange,
    placeholder,
    unknownLabel,
    loading,
    disabled,
    disableClearable = false,
    autoFocus
}: SingleSelectProps) => {
    const {t} = useTranslation();

    const {allOptions, selected} = useMemo(() => {
        const known: ResolvedOption | undefined = options.find(option => option.value === value);
        const missing = value && !known
            ? unknownOption(value, unknownLabel, isSettled(options, loading), t)
            : undefined;

        return {
            allOptions: gatherGroups<ResolvedOption>(missing ? [...options, missing] : options),
            selected: known ?? missing ?? null
        };
    }, [options, value, unknownLabel, loading, t]);

    const [inputValue, setInputValue] = useState(selected?.label ?? '');
    const searching = !!inputValue && inputValue !== selected?.label;

    // MUI filters before rendering groups; headings need that exact filtered list.
    let visibleOptions: ResolvedOption[] = [];

    return (
        <Autocomplete<ResolvedOption, false, boolean, false>
            size={'small'}
            fullWidth
            disableClearable={disableClearable || (!value && !searching)}
            clearText={t('Clear')}
            disabled={disabled}
            loading={loading}
            loadingText={t('Loading...')}
            noOptionsText={t('No options')}
            options={allOptions}
            value={selected}
            inputValue={inputValue}
            onInputChange={(_event, next) => setInputValue(next)}
            onChange={(_event, option) => {
                // Clearing a query on the unchanged empty-value option must restore its label.
                if (!option && !value) setInputValue(selected?.label ?? '');
                onChange(option?.value ?? '');
            }}
            getOptionLabel={option => option.label}
            getOptionKey={optionKey}
            getOptionDisabled={option => !!option.unavailable || !!option.disabled}
            isOptionEqualToValue={(option, candidate) => option.value === candidate.value}
            groupBy={isGrouped(allOptions) ? headingKey : undefined}
            filterOptions={candidates => {
                visibleOptions = searching ? filterBySearch(candidates, inputValue) : candidates;
                return visibleOptions;
            }}
            openOnFocus={autoFocus}
            PaperComponent={SelectListPaper}
            slotProps={{clearIndicator: {sx: {visibility: 'visible'}}}}
            renderGroup={params => <SelectGroup key={params.key} params={params} visibleOptions={visibleOptions}/>}
            renderOption={(props, option) => (
                <SelectOptionRow key={optionKey(option)} itemProps={props} option={option}/>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    InputLabelProps={{...params.InputLabelProps, shrink: true}}
                    inputProps={{
                        ...params.inputProps,
                        'aria-label': label ? params.inputProps['aria-label'] : placeholder
                    }}
                />
            )}
            sx={{
                '& .MuiAutocomplete-input': {
                    color: selected?.unavailable ? 'warning.dark' : undefined,
                    // Keep select-on-focus for typing without showing a text highlight.
                    '&::selection': {backgroundColor: 'transparent'}
                }
            }}
        />
    );
};

export default SingleSelect;
